/**
 * Main App Component - Eliza Forge
 *
 * Root application component with routing, authentication, and global providers
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { useUI } from './stores/useUI';
import { useTheme } from './stores/useTheme';

// Pages
import LoginPage from './pages/auth/LoginPage';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import SSOCallbackPage from './pages/auth/SSOCallbackPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import McpServersPage from './pages/admin/McpServersPage';
import CatalogCorrectionQueuePage from './pages/admin/CatalogCorrectionQueuePage';
import { ResumeParsingTestPage } from './pages/admin/ResumeParsingTestPage';
import CatalogAppletPage from './pages/catalog/CatalogAppletPage';
import DocumentLibrary from './pages/company-data/DocumentLibrary';
import EmployeesOverview from './pages/caylent/EmployeesOverview';
// MyInsights removed - replaced with HomePage
import { AgentConfigurationPage } from './pages/agent-configuration/AgentConfigurationPage';
import SowAutomationPage from './pages/sow/SowAutomationPage';
import ContentResearchPage from './pages/content-research/ContentResearchPage';
import PRDGeneratorPage from './pages/prd-generator/PRDGeneratorPage';
import MicrositeGeneratorPage from './pages/microsite-generator/MicrositeGeneratorPage';
import MicrositePreviewPage from './pages/microsite-generator/MicrositePreviewPage';
import ShareFolderPage from './pages/microsite-generator/ShareFolderPage';
import {
  ClientOpportunityRankerPage,
  ClientOpportunityRankerSharePage,
} from './pages/client-opportunity-ranker';
import { OpportunityDiagnosticIntelligencePage } from './pages/opportunity-diagnostic-intelligence';
import PulsePage from './pages/pulse/PulsePage';
import PulseSurveyPage from './pages/pulse/PulseSurveyPage';
import PulseWorkshopPage from './pages/pulse/PulseWorkshopPage';
import TinyModelStudioPage from './pages/tiny-model-studio/TinyModelStudioPage';
import { DataConnectionsPage } from './pages/data-connections/DataConnectionsPage';
import TTCDataProductsPage from './pages/ttc-data-products/TTCDataProductsPage';
// TalentIntelligencePage removed - functionality moved to Analysis Config
import { TalentAnalysisHistoryPage } from './pages/ml-talent/TalentAnalysisHistoryPage';
import CandidateOutreachPage from './pages/talent-intelligence/CandidateOutreachPage';
import EmailTemplatesPage from './pages/talent-intelligence/EmailTemplatesPage';
import EmailTemplateEditorPage from './pages/talent-intelligence/EmailTemplateEditorPage';
import GoogleEmailIntegrationPage from './pages/talent-intelligence/GoogleEmailIntegrationPage';
// TalentFeedbackPage removed - feedback is now a slide-in panel from the header
import AnalysisConfigPage from './pages/talent-intelligence/AnalysisConfigPage';
import TalentConfigurationPage from './pages/talent-intelligence/TalentConfigurationPage';
import { RoleBuilderPage } from './pages/talent-intelligence/RoleBuilderPage';
import { RoleBuilderSettingsPage } from './pages/talent-intelligence/RoleBuilderSettingsPage';
import ReferenceChecksPage from './pages/reference-checks/ReferenceChecksPage';
import CandidateThankYouVideosPage from './pages/candidate-videos/CandidateThankYouVideosPage';
import RecorderPage from './pages/candidate-videos/RecorderPage';
import WatchPage from './pages/candidate-videos/WatchPage';
import HomePage from './pages/home/HomePage';
import NewHomePage from './pages/home/NewHomePage';
import AdoptionDashboardPage from './pages/adoption/AdoptionDashboardPage';
import ExecutiveCommandCenterPage from './pages/executive-command-center/ExecutiveCommandCenterPage';
import BVReviewsStudioPage from './pages/bv-reviews/BVReviewsStudioPage';
import BVReviewsInsightsPage from './pages/bv-reviews/BVReviewsInsightsPage';
import BVReviewsLibraryPage from './pages/bv-reviews/BVReviewsLibraryPage';
import BVReviewsLibraryDetailPage from './pages/bv-reviews/BVReviewsLibraryDetailPage';
import BVReviewsSettingsPage from './pages/bv-reviews/BVReviewsSettingsPage';
import ComponentShowcase from './pages/design-system/ComponentShowcase';
import PlatformAdoptionAccessPage from './pages/platform-admin/AdoptionAccessPage';
import AdoptionSettingsPage from './pages/admin/AdoptionSettingsPage';

// RAG Evaluations
import EvalsPage from './pages/evals/EvalsPage';
import TelemetryPage from './pages/ai-console/TelemetryPage';

// AI Console Redesign
import AIConsoleDashboardPage from './pages/ai-console/AIConsoleDashboardPage';
import AgentLensPage from './pages/ai-console/AgentLensPage';
import AgentTestingPage from './pages/ai-console/AgentTestingPage';

// GEPA Optimizer
import GEPAOptimizerPage from './pages/gepa-optimizer/GEPAOptimizerPage';

// Prompt Management
import PromptManagementPage from './pages/prompt-management/PromptManagementPage';

// RAGFlow integrated into Data Analyst page

// Workspaces (RAGFlow Knowledge Bases)
import WorkspacesPage from './pages/domains/DomainsPage';
import WorkspaceDetailPage from './pages/domains/DomainDetailPage';
import DomainChatPage from './pages/chat/DomainChatPage';
import ChatPage from './pages/chat/ChatPage';

// Platform Admin Pages
import TenantManagementPage from './pages/platform-admin/TenantManagementPage';
import FeatureAllocationPage from './pages/platform-admin/FeatureAllocationPage';
import AdminManagementPage from './pages/platform-admin/AdminManagementPage';
import AIProvidersPage from './pages/platform-admin/AIProvidersPage';
import EmailIntegrationPage from './pages/platform-admin/EmailIntegrationPage';
import JobSchedulerPage from './pages/platform-admin/JobSchedulerPage';
import SsoPolicyPage from './pages/platform-admin/SsoPolicyPage';

// Tenant Admin Pages
import UsersRolesPage from './pages/tenant-admin/UsersRolesPage';
import ThemePage from './pages/tenant-admin/ThemePage';
import SsoSettingsPage from './pages/tenant-admin/SsoSettingsPage';
import EmailSettingsPage from './pages/tenant-admin/EmailSettingsPage';

// 13-Week Cash Flow
import {
  CashflowLayout,
  SummaryPage as CashflowSummaryPage,
  CEODashboardPage as CashflowCEOPage,
  ReceiptsPage as CashflowReceiptsPage,
  DisbursementsPage as CashflowDisbursementsPage,
  ScenariosPage as CashflowScenariosPage,
  ScenarioModelingPage as CashflowScenarioModelingPage,
  CashflowRiskRegisterPage,
  VariancePage as CashflowVariancePage,
  CashflowConfigPage,
  AvailabilityPage as CashflowAvailabilityPage,
  VarianceIntelligencePage as CashflowVarianceIntelligencePage,
  MonthlyRollupPage as CashflowMonthlyRollupPage,
  BudgetEnginePage as CashflowBudgetEnginePage,
  FPAFeedbackPage as CashflowFPAFeedbackPage,
  WorkingCapitalPage as CashflowWorkingCapitalPage,
  DriftMonitorPage as CashflowDriftMonitorPage,
  MonitorsPage as CashflowMonitorsPage,
} from './pages/cashflow';
import {
  AcquisitionSimPage as CashflowV2AcquisitionSimPage,
  AiOperationsPage as CashflowV2AiOperationsPage,
  ArCollectionsPage as CashflowV2ArCollectionsPage,
  BoardExportPage as CashflowV2BoardExportPage,
  CashTruthPage as CashflowV2CashTruthPage,
  CashflowV2Layout,
  ConfigPage as CashflowV2ConfigPage,
  DriftMonitorPage as CashflowV2DriftMonitorPage,
  EntityScorecardPage as CashflowV2EntityScorecardPage,
  ForecastGridPage as CashflowV2ForecastGridPage,
  LiquidityPulsePage as CashflowV2LiquidityPulsePage,
  VarianceIntelPage as CashflowV2VarianceIntelPage,
  WcRecommendationsPage as CashflowV2WcRecommendationsPage,
} from './pages/cashflow-v2';
import {
  VariableLagConfigPage,
  VariableLagAccrualsLayout,
  VariableLagAccrualsPage,
} from './pages/variable-lag-accruals';
import {
  CloudAccrualsConfigPage,
  CloudAccrualsLayout,
  CloudAccrualsPage,
} from './pages/cloud-accruals';
import { ContractAutomationPage } from './pages/contract-automation';
import {
  Asc606RevenueWaterfallLayout,
  Asc606RevenueWaterfallPage,
} from './pages/asc-606-revenue-waterfall';
import {
  SubledgerRevenueReconcilerLayout,
  SubledgerRevenueReconcilerPage,
} from './pages/subledger-revenue-reconciler';
import {
  DeferredRevenueSchedulerLayout,
  DeferredRevenueSchedulerPage,
} from './pages/deferred-revenue-scheduler';
import { BankReconciliationPage } from './pages/bank-reconciliation';
import { APCutoffEnforcerPage } from './pages/ap-cutoff-enforcer';
import { APInvoiceAgentPage } from './pages/ap-invoice-agent';
import { CloseCalendarPage } from './pages/close-calendar';
import { PayrollEntryPage } from './pages/payroll-entry';
import {
  JudgmentalAccrualsConfigPage,
  JudgmentalAccrualsInstructionsPage,
  JudgmentalAccrualsLayout,
  JudgmentalAccrualsSourceDataPage,
  JudgmentalAccrualsWorkpaperPage,
} from './pages/judgmental-accruals';
import {
  LagAccrualsConfigurationPage,
  LagAccrualsDashboardPage,
  LagAccrualsExportsPage,
  LagAccrualsHitlPage,
  LagAccrualsLayout,
  LagAccrualsRunbookPage,
  LagAccrualsSourceReportPage,
} from './pages/lag-accruals';

// Building - Apps in active customer development
import ComingSoonPage from './pages/coming-soon/ComingSoonPage';

// Components
import LoadingSpinner from './components/common/LoadingSpinner';
import Layout from './components/layout/Layout';
import { ToastContainer, Card, CardContent, Button, ErrorBoundary } from './components/ui';
import { useToasts } from './stores/useToasts';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { isAppletEnabled, isBaseChatPlatformEnabled, isFrontendPageEnabled } from './shared/lib/applets';
import { canAccessPulseAdmin } from './shared/lib/pulse-access';

// Toast Wrapper - Connects Zustand store to DS ToastContainer
function ToastWrapper() {
  const toasts = useToasts(s => s.toasts);
  const dismiss = useToasts(s => s.dismiss);

  // Memoize the transformation to prevent infinite re-renders
  const dsToasts = React.useMemo(() =>
    toasts.map(toast => ({
      id: toast.id,
      variant: toast.kind as 'success' | 'error' | 'info' | 'warning' | 'default',
      message: toast.message,
      title: toast.title,
      duration: toast.duration ?? 6000,
      onDismiss: dismiss,
    })),
    [toasts, dismiss]
  );

  return <ToastContainer toasts={dsToasts} position="top-right" />;
}

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requireAll?: boolean;
}

function ProtectedRoute({
  children,
  requiredPermissions = [],
  requireAll = false
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyPermission, hasAllPermissions } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions if specified
  if (requiredPermissions.length > 0) {
    const hasPermission = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasPermission) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center p-6">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center">
              <ShieldExclamationIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="font-title text-2xl text-charcoal dark:text-gray-100 mb-4">
                Access Denied
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                You don't have permission to access this page.
              </p>
              <Button onClick={() => window.history.back()}>
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  return <>{children}</>;
}

// Public Route Component (redirects to dashboard if authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}

// App Routes Component
function AppRoutes() {
  const { user } = useAuth();
  const isRagEnabled = isBaseChatPlatformEnabled();
  const isAdoptionEnabled = isFrontendPageEnabled('adoption');
  const isExecutiveCommandCenterEnabled = isFrontendPageEnabled('executive-command-center');
  const isTalentEnabled = isAppletEnabled('talent');
  const isSowEnabled = isFrontendPageEnabled('sow');
  const isContentWriterEnabled = isFrontendPageEnabled('content-research');
  const isPrdGeneratorEnabled = isFrontendPageEnabled('prd-generator');
  const isMicrositeGeneratorEnabled = isFrontendPageEnabled('microsite-generator');
  const isClientOpportunityRankerEnabled = isFrontendPageEnabled('client-opportunity-ranker');
  const isOpportunityDiagnosticIntelligenceEnabled = isFrontendPageEnabled('opportunity-diagnostic-intelligence');
  const isPulseEnabled = isFrontendPageEnabled('pulse');
  const canUsePulseAdmin = canAccessPulseAdmin(user);
  const isRoleBuilderPageEnabled = isFrontendPageEnabled('role-builder');
  const isDataConnectionsEnabled = isFrontendPageEnabled('data-connections');
  const isTTCDataProductsEnabled = isFrontendPageEnabled('ttc-data-products');
  const isCatalogEnabled = isFrontendPageEnabled('catalog');
  const isEvalsEnabled = isFrontendPageEnabled('evals');
  const isTelemetryEnabled = isFrontendPageEnabled('telemetry');
  const isGepaOptimizerEnabled = isFrontendPageEnabled('gepa-optimizer');
  const isPromptManagementEnabled = isFrontendPageEnabled('prompt-management');
  const isCashflowEnabled = isFrontendPageEnabled('cashflow');
  const isCashflowV2Enabled = isFrontendPageEnabled('cashflow_v2');
  const isVariableLagAccrualsEnabled = isFrontendPageEnabled('variable-lag-accruals');
  const isBankReconciliationEnabled = isFrontendPageEnabled('bank_reconciliation');
  const isAPCutoffEnforcerEnabled = isFrontendPageEnabled('ap-cutoff-enforcer');
  const isAPInvoiceAgentEnabled = isFrontendPageEnabled('ap-invoice-agent');
  const isCloseCalendarEnabled = isFrontendPageEnabled('close_calendar');
  const isPayrollEntryEnabled = isFrontendPageEnabled('payroll-entry');
  const isJudgmentalAccrualsEnabled = isFrontendPageEnabled('judgmental-accruals');
  const isLagAccrualsEnabled = isFrontendPageEnabled('lag_accruals');
  const isCloudAccrualsEnabled = isFrontendPageEnabled('cloud-accruals');
  const isContractAutomationEnabled = isFrontendPageEnabled('contract-automation');
  const isAsc606RevenueWaterfallEnabled = isFrontendPageEnabled('asc-606-revenue-waterfall');
  const isDeferredRevenueSchedulerEnabled = isFrontendPageEnabled('deferred-revenue-scheduler');
  const isSubledgerRevenueReconcilerEnabled = isFrontendPageEnabled('subledger-revenue-reconciler');
  const isComingSoonEnabled = isFrontendPageEnabled('coming-soon');
  const isBVReviewsStudioEnabled = isFrontendPageEnabled('bv-reviews-studio');
  const isBVReviewsInsightsEnabled = isFrontendPageEnabled('bv-reviews-insights');
  const isBVReviewsLibraryEnabled = isFrontendPageEnabled('bv-reviews-library');
  const isBVReviewsSettingsEnabled = isFrontendPageEnabled('bv-reviews-settings');
  const isCandidateVideosEnabled = isFrontendPageEnabled('candidate-thank-you-videos');
  const bvReviewsLandingPath =
    (isBVReviewsStudioEnabled && '/bv-reviews/studio')
    || (isBVReviewsInsightsEnabled && '/bv-reviews/insights')
    || (isBVReviewsLibraryEnabled && '/bv-reviews/library')
    || (isBVReviewsSettingsEnabled && '/bv-reviews/settings')
    || null;

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Accept Invite - Public route for new users */}
      <Route
        path="/accept-invite"
        element={<AcceptInvitePage />}
      />

      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />

      <Route path="/r/:requestToken" element={<RecorderPage />} />
      <Route path="/watch/:watchToken" element={<WatchPage />} />

      {/* SSO callback route (token handoff from backend redirect) */}
      <Route
        path="/auth/sso/callback"
        element={<SSOCallbackPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />

      {/* Home - User customizable dashboard */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <NewHomePage />
          </ProtectedRoute>
        }
      />

      {/* Old Home - Widget dashboard (legacy) */}
      <Route
        path="/home/widgets"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects to Workspaces */}
      {isRagEnabled && (
        <>
          <Route path="/knowledge-base" element={<Navigate to="/workspaces" replace />} />
          <Route path="/domains" element={<Navigate to="/workspaces" replace />} />
          <Route path="/domains/:workspaceId" element={<Navigate to="/workspaces/:workspaceId" replace />} />
        </>
      )}

      {/* Workspaces (RAGFlow Knowledge Bases) */}
      {isRagEnabled && (
        <>
          <Route
            path="/workspaces"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'documents:write', 'platform:admin']} requireAll={false}>
                <WorkspacesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspaces/:workspaceId"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'documents:write', 'platform:admin']} requireAll={false}>
                <WorkspaceDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspaces/:workspaceId/:workspaceSection"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'documents:write', 'platform:admin']} requireAll={false}>
                <WorkspaceDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspaces/:workspaceId/semantic/:semanticSurface"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'documents:write', 'platform:admin']} requireAll={false}>
                <WorkspaceDetailPage />
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isRagEnabled && (
        <Route
          path="/documents/library"
          element={
            <ProtectedRoute requiredPermissions={['assistant:documents:read', 'platform:admin']} requireAll={false}>
              <Layout>
                <DocumentLibrary />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}

      {/* Search Routes */}
      <Route
        path="/search"
        element={
          <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
            <div className="p-8 text-center">
              <h1 className="text-2xl font-bold text-text">Search</h1>
              <p className="text-muted mt-2">Document search coming soon...</p>
            </div>
          </ProtectedRoute>
        }
      />



      {/* Caylent Routes */}
      <Route
        path="/caylent/employees"
        element={
          <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
            <EmployeesOverview />
          </ProtectedRoute>
        }
      />

      {/* Chat Routes (New) */}
      {isRagEnabled && (
        <>
          <Route
            path="/chat"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat/:workspaceId"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat/:workspaceId/:conversationId"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
                <ChatPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {isSowEnabled && (
        <Route
          path="/sow"
          element={
            <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
              <SowAutomationPage />
            </ProtectedRoute>
          }
        />
      )}
      {isContentWriterEnabled && (
        <Route
          path="/content-research"
          element={
            <ProtectedRoute requiredPermissions={['content_writer:read', 'content_writer:write', 'platform:admin']} requireAll={false}>
              <ContentResearchPage />
            </ProtectedRoute>
          }
        />
      )}
      {isPrdGeneratorEnabled && (
        <Route
          path="/prd-generator"
          element={
            <ProtectedRoute requiredPermissions={['prd_generator:read', 'prd_generator:write', 'platform:admin']} requireAll={false}>
              <PRDGeneratorPage />
            </ProtectedRoute>
          }
        />
      )}
      {isMicrositeGeneratorEnabled && (
        <>
          <Route
            path="/microsite-preview/link/:token"
            element={<MicrositePreviewPage />}
          />
          <Route
            path="/microsite-preview/public/:publicToken"
            element={<MicrositePreviewPage />}
          />
          <Route
            path="/share/link/:token"
            element={<ShareFolderPage />}
          />
          <Route
            path="/share/*"
            element={<ShareFolderPage />}
          />
          <Route
            path="/microsite-generator"
            element={
              <ProtectedRoute requiredPermissions={['microsite_generator:read', 'platform:admin']} requireAll={false}>
                <MicrositeGeneratorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/microsite-preview/:id"
            element={
              <ProtectedRoute requiredPermissions={['microsite_generator:read', 'platform:admin']} requireAll={false}>
                <MicrositePreviewPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {isClientOpportunityRankerEnabled && (
        <>
          <Route
            path="/opportunity-ranker/share/:publicToken"
            element={<ClientOpportunityRankerSharePage />}
          />
          <Route
            path="/client-opportunity-ranker/share/:publicToken"
            element={<ClientOpportunityRankerSharePage />}
          />
          <Route
            path="/client-opportunity-ranker"
            element={
              <ProtectedRoute requiredPermissions={['client_opportunity_ranker:read', 'platform:admin']} requireAll={false}>
                <ClientOpportunityRankerPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {isOpportunityDiagnosticIntelligenceEnabled && (
        <Route
          path="/opportunity-diagnostic-intelligence"
          element={
            <ProtectedRoute requiredPermissions={['opportunity_diagnostic_intelligence:read', 'platform:admin']} requireAll={false}>
              <OpportunityDiagnosticIntelligencePage />
            </ProtectedRoute>
          }
        />
      )}
      {isPulseEnabled && (
        <>
          {canUsePulseAdmin && (
            <>
              <Route
                path="/pulse"
                element={
                  <ProtectedRoute>
                    <PulsePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pulse/workshops/:workshopId"
                element={
                  <ProtectedRoute>
                    <PulseWorkshopPage />
                  </ProtectedRoute>
                }
              />
            </>
          )}
          <Route
            path="/pulse/:organizationSlug/:workshopKey"
            element={<PulseSurveyPage />}
          />
        </>
      )}

      {/* Legacy BI routes - redirect to new Chat */}
      {isRagEnabled && (
        <>
          <Route path="/data-analyst" element={<Navigate to="/chat" replace />} />
          <Route path="/data-analyst/insurance" element={<Navigate to="/chat" replace />} />
          <Route path="/data-analyst/fasb" element={<Navigate to="/chat" replace />} />
          <Route path="/business-intelligence" element={<Navigate to="/chat" replace />} />
        </>
      )}

      {/* Legacy Data Search routes - migrated to Chat */}
      {isRagEnabled && (
        <>
          <Route path="/data-search" element={<Navigate to="/chat" replace />} />
          <Route path="/data-search/:conversationId" element={<Navigate to="/chat" replace />} />
        </>
      )}

      {/* Bazaarvoice Review Intelligence Routes */}
      {bvReviewsLandingPath && (
        <Route
          path="/bv-reviews"
          element={
            <ProtectedRoute requiredPermissions={['bv:read', 'bv:write', 'bv:admin', 'platform:admin']} requireAll={false}>
              <Navigate to={bvReviewsLandingPath} replace />
            </ProtectedRoute>
          }
        />
      )}
      {isBVReviewsStudioEnabled && (
        <>
          <Route
            path="/bv-reviews/studio"
            element={
              <ProtectedRoute requiredPermissions={['bv:read', 'bv:write', 'platform:admin']} requireAll={false}>
                <BVReviewsStudioPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bv-reviews/studio/:id"
            element={
              <ProtectedRoute requiredPermissions={['bv:read', 'bv:write', 'platform:admin']} requireAll={false}>
                <BVReviewsStudioPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {isBVReviewsInsightsEnabled && (
        <>
          <Route
            path="/bv-reviews/insights"
            element={
              <ProtectedRoute requiredPermissions={['bv:read', 'bv:write', 'platform:admin']} requireAll={false}>
                <BVReviewsInsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bv-reviews/insights/:convId"
            element={
              <ProtectedRoute requiredPermissions={['bv:read', 'bv:write', 'platform:admin']} requireAll={false}>
                <BVReviewsInsightsPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {isBVReviewsLibraryEnabled && (
        <>
          <Route
            path="/bv-reviews/library"
            element={
              <ProtectedRoute requiredPermissions={['bv:read', 'bv:write', 'platform:admin']} requireAll={false}>
                <BVReviewsLibraryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bv-reviews/library/:id"
            element={
              <ProtectedRoute requiredPermissions={['bv:read', 'bv:write', 'platform:admin']} requireAll={false}>
                <BVReviewsLibraryDetailPage />
              </ProtectedRoute>
            }
          />
        </>
      )}
      {isBVReviewsSettingsEnabled && (
        <Route
          path="/bv-reviews/settings"
          element={
            <ProtectedRoute requiredPermissions={['bv:admin', 'platform:admin']} requireAll={false}>
              <BVReviewsSettingsPage />
            </ProtectedRoute>
          }
        />
      )}

      {/* Data Connections Routes */}
      {isDataConnectionsEnabled && (
        <Route
          path="/data-connections"
          element={
            <ProtectedRoute requiredPermissions={['connections:read', 'platform:admin']} requireAll={false}>
              <Layout>
                <DataConnectionsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}

      {isTTCDataProductsEnabled && (
        <Route
          path="/ttc-data-products"
          element={
            <ProtectedRoute requiredPermissions={['platform:admin']}>
              <Layout>
                <TTCDataProductsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}

      {isCatalogEnabled && (
        <>
          <Route
            path="/catalog"
            element={
              <ProtectedRoute requiredPermissions={['connections:read', 'catalog:admin', 'platform:admin']} requireAll={false}>
                <Layout>
                  <CatalogAppletPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalog/assets"
            element={
              <ProtectedRoute requiredPermissions={['connections:read', 'catalog:admin', 'platform:admin']} requireAll={false}>
                <Layout>
                  <CatalogAppletPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalog/assets/:assetId"
            element={
              <ProtectedRoute requiredPermissions={['connections:read', 'catalog:admin', 'platform:admin']} requireAll={false}>
                <Layout>
                  <CatalogAppletPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalog/corrections"
            element={
              <ProtectedRoute requiredPermissions={['connections:read', 'catalog:admin', 'platform:admin']} requireAll={false}>
                <Layout>
                  <CatalogAppletPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalog/providers"
            element={
              <ProtectedRoute requiredPermissions={['connections:read', 'catalog:admin', 'platform:admin']} requireAll={false}>
                <Layout>
                  <CatalogAppletPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalog/settings"
            element={
              <ProtectedRoute requiredPermissions={['connections:read', 'catalog:admin', 'platform:admin']} requireAll={false}>
                <Layout>
                  <CatalogAppletPage />
                </Layout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {/* Talent Intelligence Routes */}
      {/* TalentIntelligencePage removed - functionality moved to Analysis Config */}
      {isTalentEnabled && <Route
        path="/talent/history"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:history:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <TalentAnalysisHistoryPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && <Route
        path="/talent/outreach"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:results:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <CandidateOutreachPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && <Route
        path="/talent/email-templates"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:email_templates:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <EmailTemplatesPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && <Route
        path="/talent/email-templates/new"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:email_templates:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <EmailTemplateEditorPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && <Route
        path="/talent/email-templates/:id"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:email_templates:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <EmailTemplateEditorPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && <Route
        path="/talent/email-integration"
        element={
          <ProtectedRoute requiredPermissions={['admin:settings:read', 'admin:settings:update', 'platform:admin']} requireAll={false}>
            <Layout>
              <GoogleEmailIntegrationPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && <Route
        path="/talent/settings/email"
        element={<Navigate to="/talent/email-integration" replace />}
      />}
      {isTalentEnabled && <Route
        path="/talent/analysis-config"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:config:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <AnalysisConfigPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && <Route
        path="/talent/configuration"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:blueprints:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <TalentConfigurationPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && isRoleBuilderPageEnabled && <Route
        path="/talent/role-builder"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:role_builder:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <RoleBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />}
      {isTalentEnabled && isRoleBuilderPageEnabled && <Route
        path="/talent/role-builder/settings"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:role_builder:write', 'platform:admin']} requireAll={false}>
            <Layout>
              <RoleBuilderSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />}

      {isTalentEnabled && isCandidateVideosEnabled && <Route
        path="/talent/thank-you-videos"
        element={
          <ProtectedRoute requiredPermissions={['candidate_video:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <CandidateThankYouVideosPage />
            </Layout>
          </ProtectedRoute>
        }
      />}

      {/* Reference Check Voice Agent */}
      {isTalentEnabled && <Route
        path="/reference-checks"
        element={
          <ProtectedRoute requiredPermissions={['recruiter:reference_checks:access', 'platform:admin']} requireAll={false}>
            <Layout>
              <ReferenceChecksPage />
            </Layout>
          </ProtectedRoute>
        }
      />}

      {/* 13-Week Cash Flow Routes */}
      {isCashflowEnabled && (
        <>
          <Route
            path="/cashflow"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowSummaryPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/ceo"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowCEOPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/receipts"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowReceiptsPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/disbursements"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowDisbursementsPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/scenarios"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowScenariosPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/scenario-modeling"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowScenarioModelingPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/risk-register"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowRiskRegisterPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/variance"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowVariancePage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/configuration"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:admin', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowConfigPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/availability"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowAvailabilityPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/variance-intelligence"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowVarianceIntelligencePage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/monthly-rollup"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowMonthlyRollupPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/budget-engine"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowBudgetEnginePage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/fpa-feedback"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowFPAFeedbackPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/working-capital"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowWorkingCapitalPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/monitors"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowMonitorsPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow/drift-monitor"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowLayout><CashflowDriftMonitorPage /></CashflowLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isCashflowV2Enabled && (
        <>
          <Route
            path="/cashflow-v2"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2LiquidityPulsePage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/liquidity/cash-truth"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2CashTruthPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/liquidity/drift"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2DriftMonitorPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/forecast/grid"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2ForecastGridPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/forecast/variance"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2VarianceIntelPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/portfolio/entities"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2EntityScorecardPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/portfolio/acquisition"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2AcquisitionSimPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/working-capital/collections"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2ArCollectionsPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/working-capital/recommendations"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2WcRecommendationsPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/reporting/exports"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2BoardExportPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/admin/ai-ops"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:admin', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2AiOperationsPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow-v2/admin/configuration"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:admin', 'platform:admin']} requireAll={false}>
                <CashflowV2Layout><CashflowV2ConfigPage /></CashflowV2Layout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isBankReconciliationEnabled && (
        <Route
          path="/bank-reconciliation"
          element={
            <ProtectedRoute requiredPermissions={['bank_reconciliation:read', 'platform:admin']} requireAll={false}>
              <Layout>
                <BankReconciliationPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}

      {isCloseCalendarEnabled && (
        <Route
          path="/close-calendar/*"
          element={
            <ProtectedRoute requiredPermissions={['close_calendar:read', 'platform:admin']} requireAll={false}>
              <Layout>
                <CloseCalendarPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}

      {isPayrollEntryEnabled && (
        <Route
          path="/payroll-entry/*"
          element={
            <ProtectedRoute requiredPermissions={['payroll:read', 'platform:admin']} requireAll={false}>
              <PayrollEntryPage />
            </ProtectedRoute>
          }
        />
      )}

      {isAPCutoffEnforcerEnabled && (
        <Route
          path="/ap-cutoff-enforcer/*"
          element={
            <ProtectedRoute requiredPermissions={['ap_cutoff:read', 'platform:admin']} requireAll={false}>
              <APCutoffEnforcerPage />
            </ProtectedRoute>
          }
        />
      )}

      {isAPInvoiceAgentEnabled && (
        <Route
          path="/ap-invoice-agent/*"
          element={
            <ProtectedRoute requiredPermissions={['ap_invoice:read', 'platform:admin']} requireAll={false}>
              <APInvoiceAgentPage />
            </ProtectedRoute>
          }
        />
      )}

      {isJudgmentalAccrualsEnabled && (
        <>
          <Route
            path="/judgmental-accruals"
            element={
              <ProtectedRoute requiredPermissions={['judgmental_accruals:read', 'platform:admin']} requireAll={false}>
                <JudgmentalAccrualsLayout><JudgmentalAccrualsWorkpaperPage /></JudgmentalAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/judgmental-accruals/source-log"
            element={
              <ProtectedRoute requiredPermissions={['judgmental_accruals:read', 'platform:admin']} requireAll={false}>
                <JudgmentalAccrualsLayout><JudgmentalAccrualsSourceDataPage /></JudgmentalAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/judgmental-accruals/instructions"
            element={
              <ProtectedRoute requiredPermissions={['judgmental_accruals:read', 'platform:admin']} requireAll={false}>
                <JudgmentalAccrualsLayout><JudgmentalAccrualsInstructionsPage /></JudgmentalAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/judgmental-accruals/config"
            element={
              <ProtectedRoute requiredPermissions={['judgmental_accruals:write', 'platform:admin']} requireAll={false}>
                <JudgmentalAccrualsLayout><JudgmentalAccrualsConfigPage /></JudgmentalAccrualsLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isLagAccrualsEnabled && (
        <>
          <Route
            path="/lag-accruals"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <LagAccrualsLayout><LagAccrualsDashboardPage /></LagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lag-accruals/hitl"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:write', 'platform:admin']} requireAll={false}>
                <LagAccrualsLayout><LagAccrualsHitlPage /></LagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lag-accruals/exports"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:export', 'platform:admin']} requireAll={false}>
                <LagAccrualsLayout><LagAccrualsExportsPage /></LagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lag-accruals/runbook"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <LagAccrualsLayout><LagAccrualsRunbookPage /></LagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lag-accruals/source-report"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <LagAccrualsLayout><LagAccrualsSourceReportPage /></LagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lag-accruals/admin/configuration"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:admin', 'platform:admin']} requireAll={false}>
                <LagAccrualsLayout><LagAccrualsConfigurationPage /></LagAccrualsLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isVariableLagAccrualsEnabled && (
        <>
          <Route
            path="/variable-lag-accruals"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <VariableLagAccrualsLayout><VariableLagAccrualsPage /></VariableLagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/variable-lag-accruals/review"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <VariableLagAccrualsLayout><VariableLagAccrualsPage view="review" /></VariableLagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/variable-lag-accruals/sources"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <VariableLagAccrualsLayout><VariableLagAccrualsPage view="sources" /></VariableLagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/variable-lag-accruals/exports"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <VariableLagAccrualsLayout><VariableLagAccrualsPage view="exports" /></VariableLagAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/variable-lag-accruals/configuration"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:admin', 'platform:admin']} requireAll={false}>
                <VariableLagAccrualsLayout><VariableLagConfigPage /></VariableLagAccrualsLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isCloudAccrualsEnabled && (
        <>
          <Route
            path="/cloud-accruals"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CloudAccrualsLayout><CloudAccrualsPage /></CloudAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cloud-accruals/review"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CloudAccrualsLayout><CloudAccrualsPage view="review" /></CloudAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cloud-accruals/sources"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CloudAccrualsLayout><CloudAccrualsPage view="sources" /></CloudAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cloud-accruals/exports"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <CloudAccrualsLayout><CloudAccrualsPage view="exports" /></CloudAccrualsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cloud-accruals/configuration"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:admin', 'platform:admin']} requireAll={false}>
                <CloudAccrualsLayout><CloudAccrualsConfigPage /></CloudAccrualsLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isAsc606RevenueWaterfallEnabled && (
        <>
          <Route
            path="/asc-606-revenue-waterfall"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <Asc606RevenueWaterfallLayout><Asc606RevenueWaterfallPage /></Asc606RevenueWaterfallLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/asc-606-revenue-waterfall/review"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <Asc606RevenueWaterfallLayout><Asc606RevenueWaterfallPage view="review" /></Asc606RevenueWaterfallLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/asc-606-revenue-waterfall/sources"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <Asc606RevenueWaterfallLayout><Asc606RevenueWaterfallPage view="sources" /></Asc606RevenueWaterfallLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/asc-606-revenue-waterfall/exports"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <Asc606RevenueWaterfallLayout><Asc606RevenueWaterfallPage view="exports" /></Asc606RevenueWaterfallLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isContractAutomationEnabled && (
        <Route
          path="/contract-automation"
          element={
            <ProtectedRoute requiredPermissions={['contract_automation:read', 'platform:admin']} requireAll={false}>
              <ContractAutomationPage />
            </ProtectedRoute>
          }
        />
      )}

      {isDeferredRevenueSchedulerEnabled && (
        <>
          <Route
            path="/deferred-revenue-scheduler"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <DeferredRevenueSchedulerLayout><DeferredRevenueSchedulerPage /></DeferredRevenueSchedulerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deferred-revenue-scheduler/review"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <DeferredRevenueSchedulerLayout><DeferredRevenueSchedulerPage view="review" /></DeferredRevenueSchedulerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deferred-revenue-scheduler/sources"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <DeferredRevenueSchedulerLayout><DeferredRevenueSchedulerPage view="sources" /></DeferredRevenueSchedulerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deferred-revenue-scheduler/exports"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <DeferredRevenueSchedulerLayout><DeferredRevenueSchedulerPage view="exports" /></DeferredRevenueSchedulerLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {isSubledgerRevenueReconcilerEnabled && (
        <>
          <Route
            path="/subledger-revenue-reconciler"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <SubledgerRevenueReconcilerLayout><SubledgerRevenueReconcilerPage /></SubledgerRevenueReconcilerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subledger-revenue-reconciler/review"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <SubledgerRevenueReconcilerLayout><SubledgerRevenueReconcilerPage view="review" /></SubledgerRevenueReconcilerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subledger-revenue-reconciler/sources"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <SubledgerRevenueReconcilerLayout><SubledgerRevenueReconcilerPage view="sources" /></SubledgerRevenueReconcilerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subledger-revenue-reconciler/exports"
            element={
              <ProtectedRoute requiredPermissions={['cashflow:read', 'platform:admin']} requireAll={false}>
                <SubledgerRevenueReconcilerLayout><SubledgerRevenueReconcilerPage view="exports" /></SubledgerRevenueReconcilerLayout>
              </ProtectedRoute>
            }
          />
        </>
      )}

      {/* Adoption Dashboard */}
      {isAdoptionEnabled && <Route
        path="/adoption"
        element={
          <ProtectedRoute requiredPermissions={['adoption:view_dashboard', 'platform:admin']} requireAll={false}>
            <AdoptionDashboardPage />
          </ProtectedRoute>
        }
      />}

      {isExecutiveCommandCenterEnabled && <Route
        path="/executive-command-center"
        element={
          <ProtectedRoute requiredPermissions={['ecc:read', 'platform:admin']} requireAll={false}>
            <ExecutiveCommandCenterPage />
          </ProtectedRoute>
        }
      />}

      {/* Admin Routes - Integrated into main layout */}
      <Route
        path="/admin/documents"
        element={
          <ProtectedRoute requiredPermissions={['assistant:documents:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <div className="p-8 text-center">
                <h1 className="text-2xl font-bold text-text">Document Management</h1>
                <p className="text-muted mt-2">Document management coming soon...</p>
              </div>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-models"
        element={
          <ProtectedRoute requiredPermissions={['admin:settings:providers:manage', 'platform:admin']} requireAll={false}>
            <Layout>
              <div className="p-8 text-center">
                <h1 className="text-2xl font-bold text-text">AI Models</h1>
                <p className="text-muted mt-2">AI model management coming soon...</p>
              </div>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute requiredPermissions={['audit:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <div className="p-8 text-center">
                <h1 className="text-2xl font-bold text-text">Analytics</h1>
                <p className="text-muted mt-2">Analytics coming soon...</p>
              </div>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/catalog/corrections"
        element={
          <ProtectedRoute requiredPermissions={['catalog:admin', 'platform:admin']} requireAll={false}>
            <Layout>
              <CatalogCorrectionQueuePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requiredPermissions={['admin:settings:read', 'platform:admin']} requireAll={false}>
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/mcp-servers"
        element={
          <ProtectedRoute requiredPermissions={['admin:settings:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <McpServersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/adoption-settings"
        element={
          <ProtectedRoute requiredPermissions={['adoption:manage_sync', 'adoption:manage_sharing', 'platform:admin']} requireAll={false}>
            <AdoptionSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/adoption-access"
        element={
          <Navigate to="/admin/adoption-settings" replace />
        }
      />
      <Route
        path="/admin/resume-parsing-test"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <ResumeParsingTestPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents"
        element={
          <ProtectedRoute requiredPermissions={['labs:agents:access', 'platform:admin']} requireAll={false}>
            <AgentConfigurationPage />
          </ProtectedRoute>
        }
      />

      {/* AI Console - RAG Evaluations, GEPA Optimizer, Prompt Management */}
      {/* These pages require ai_console:access OR platform:admin */}
      {isEvalsEnabled && <Route
        path="/evals"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:access', 'platform:admin']}>
            <EvalsPage />
          </ProtectedRoute>
        }
      />}

      {isTelemetryEnabled && <Route
        path="/telemetry"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:access', 'assistant:access', 'platform:admin']}>
            <TelemetryPage />
          </ProtectedRoute>
        }
      />}

      {/* AI Console Redesign — coexists with old pages on new routes */}
      <Route
        path="/ai-console/dashboard"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:access']}>
            <AIConsoleDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-console/agent-lens"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:access']}>
            <AgentLensPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-console/agent-testing"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:sandbox']}>
            <AgentTestingPage />
          </ProtectedRoute>
        }
      />
      {isGepaOptimizerEnabled && <Route
        path="/gepa-optimizer"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:access', 'platform:admin']}>
            <GEPAOptimizerPage />
          </ProtectedRoute>
        }
      />}

      {isPromptManagementEnabled && <Route
        path="/prompt-management"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:access', 'platform:admin']}>
            <PromptManagementPage />
          </ProtectedRoute>
        }
      />}

      {isFrontendPageEnabled('tiny-model-studio') && <Route
        path="/ai-console/tiny-model-studio"
        element={
          <ProtectedRoute requiredPermissions={['ai_console:access', 'assistant:access', 'platform:admin']}>
            <TinyModelStudioPage />
          </ProtectedRoute>
        }
      />}

      {/* Platform Admin Routes (Eliza Super Admins) */}
      {/* All platform admin pages are now flat under /platform-admin/ */}
      <Route
        path="/platform-admin/admins"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <AdminManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform-admin/providers"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <AIProvidersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform-admin/email"
        element={
          <ProtectedRoute requiredPermissions={['system:admin']}>
            <Layout>
              <EmailIntegrationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform-admin/jobs"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <JobSchedulerPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/jobs"
        element={
          <ProtectedRoute requiredPermissions={['adoption:manage_jobs', 'platform:admin']} requireAll={false}>
            <Layout>
              <JobSchedulerPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform-admin/sso-policy"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <SsoPolicyPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform-admin/tenants"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <TenantManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform-admin/features"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <FeatureAllocationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform-admin/adoption"
        element={
          <ProtectedRoute requiredPermissions={['platform:admin']}>
            <Layout>
              <PlatformAdoptionAccessPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Legacy routes - redirect old paths to new flat structure */}
      <Route
        path="/platform-admin/settings"
        element={<Navigate to="/platform-admin/admins" replace />}
      />
      <Route
        path="/platform-admin/settings/providers"
        element={<Navigate to="/platform-admin/providers" replace />}
      />
      <Route
        path="/platform-admin/settings/email"
        element={<Navigate to="/platform-admin/email" replace />}
      />
      <Route
        path="/platform-admin/settings/jobs"
        element={<Navigate to="/platform-admin/jobs" replace />}
      />
      <Route
        path="/platform-admin/tenants/features"
        element={<Navigate to="/platform-admin/features" replace />}
      />
      <Route
        path="/platform-admin/tenants/adoption"
        element={<Navigate to="/platform-admin/adoption" replace />}
      />

      {/* Tenant Admin Routes (Customer Admins) */}
      <Route
        path="/tenant-admin/users"
        element={
          <ProtectedRoute requiredPermissions={['users:read', 'roles:read', 'platform:admin']} requireAll={false}>
            <Layout>
              <UsersRolesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant-admin/theme"
        element={
          <ProtectedRoute requiredPermissions={['theme:write', 'platform:admin']} requireAll={false}>
            <Layout>
              <ThemePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant-admin/storage"
        element={<Navigate to="/data-connections?type=s3" replace />}
      />
      <Route
        path="/tenant-admin/sso"
        element={
          <ProtectedRoute requiredPermissions={['admin:settings:update', 'platform:admin']} requireAll={false}>
            <Layout>
              <SsoSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant-admin/email"
        element={
          <ProtectedRoute requiredPermissions={['admin:settings:read', 'admin:settings:update', 'platform:admin']} requireAll={false}>
            <Layout>
              <EmailSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Profile Routes */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <div className="p-8 text-center">
              <h1 className="text-2xl font-bold text-text">Profile Settings</h1>
              <p className="text-muted mt-2">Profile settings coming soon...</p>
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <div className="p-8 text-center">
              <h1 className="text-2xl font-bold text-text">Settings</h1>
              <p className="text-muted mt-2">Settings coming soon...</p>
            </div>
          </ProtectedRoute>
        }
      />

      {/* Building - Apps in active customer development */}
      {isComingSoonEnabled && (
        <>
          <Route
            path="/deal-intelligence"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="deal-intelligence" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rfp-responder"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="rfp-responder" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-health"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="customer-health" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ma-analyst"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="ma-analyst" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spend-optimizer"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="spend-optimizer" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/regulatory-radar"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="regulatory-radar" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding-copilot"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="onboarding-copilot" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/competitive-intel"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="competitive-intel" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaign-optimizer"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="campaign-optimizer" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contract-hub"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="contract-hub" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/it-service-desk"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="it-service-desk" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/supply-chain"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="supply-chain" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product-insights"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="product-insights" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/eliza-engage"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="eliza-engage" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/incident-commander"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="incident-commander" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-intelligence"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="partner-intelligence" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/board-report-generator"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="board-report-generator" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge-miner"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="knowledge-miner" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-guardian"
            element={
              <ProtectedRoute requiredPermissions={['platform:admin']} requireAll={false}>
                <ComingSoonPage appId="brand-guardian" />
              </ProtectedRoute>
            }
          />
        </>
      )}

      {/* Design System - Component Showcase (public for development) */}
      <Route
        path="/design-system"
        element={<ComponentShowcase />}
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />

      {/* Legacy Domain Chat - URL: /:domainName/:conversationUuid */}
      {/* These catch-all routes handle legacy URLs and redirect to new structure */}
      {isRagEnabled && (
        <>
          <Route
            path="/:domainName/:conversationUuid"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
                <DomainChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:domainName"
            element={
              <ProtectedRoute requiredPermissions={['assistant:access', 'platform:admin']} requireAll={false}>
                <DomainChatPage />
              </ProtectedRoute>
            }
          />
        </>
      )}

      {/* 404 Page */}
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center p-6">
            <Card className="max-w-md w-full">
              <CardContent className="p-8 text-center">
                <h1 className="font-title text-display text-charcoal dark:text-gray-100 mb-4">
                  404
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  The page you're looking for doesn't exist.
                </p>
                <Button onClick={() => window.history.back()}>
                  Go Back
                </Button>
              </CardContent>
            </Card>
          </div>
        }
      />
    </Routes>
  );
}

// Main App Component
function App() {
  const theme = useUI((s) => s.theme);
  const loadTheme = useTheme((s) => s.loadTheme);

  // Track OS preference when using system theme
  const [osPrefersDark, setOsPrefersDark] = React.useState<boolean | null>(null);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setOsPrefersDark(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  // Load tenant theme on app initialization
  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  useEffect(() => {
    const root = document.documentElement;
    const resolved = theme === 'system'
      ? (osPrefersDark ? 'dark' : 'light')
      : theme;

    // Set data-theme attribute for CSS custom properties
    root.setAttribute('data-theme', resolved);

    // Add/remove 'dark' class for Tailwind dark mode
    if (resolved === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    const meta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement | null;
    if (meta) {
      meta.content = resolved === 'dark' ? '#0F172A' : '#F9FAFB';  // DS slate-900 / gray-50
    }
  }, [theme, osPrefersDark]);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <NavigationProvider>
            <div className="App">
              <AppRoutes />
              <ToastWrapper />
            </div>
          </NavigationProvider>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
